import { AuthService } from './AuthService.js';

// Short debounce so an aggressive reload (Ctrl+Shift+R on browsers that skip
// beforeunload) loses at most ~1s of progress. The old 10s window was a reasonable
// server-load optimisation but meant gold gained mid-wave could disappear on reload.
const SYNC_DEBOUNCE_MS = 1_000;

function getApiBase(): string {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined' && window.location?.hostname !== 'localhost') return '';
  return 'http://localhost:4000';
}

export class SyncService {
  private static instance: SyncService;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingSlot: number | null = null;
  private pendingData: unknown = null;

  private constructor() {}

  static getInstance(): SyncService {
    if (!SyncService.instance) SyncService.instance = new SyncService();
    return SyncService.instance;
  }

  static _resetInstance(): void { (SyncService as any).instance = undefined; }

  private authHeaders(): Record<string, string> {
    const token = AuthService.getInstance().getToken();
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  }

  /** Push a single save slot to server (trailing-edge debounced — always sends the latest state).
   *  Pass immediate=true to bypass the debounce timer (used for critical saves like stage transitions). */
  async syncSave(slot: number, data: unknown, immediate = false): Promise<void> {
    if (!AuthService.getInstance().isLoggedIn()) return;
    this.pendingSlot = slot;
    this.pendingData = data;
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    if (immediate) {
      this.debounceTimer = null;
      void this._sendPending();
      return;
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this._sendPending();
    }, SYNC_DEBOUNCE_MS);
  }

  /** Drop any pending debounced save without sending it. Call on logout so the next user
   *  can't accidentally POST the previous user's state under their own auth token. */
  clearPending(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.pendingSlot = null;
    this.pendingData = null;
  }

  /** Immediately flush any pending debounced save to the server. Call on page close/hide.
   *  Uses keepalive:true so the browser does not cancel the request during page teardown. */
  flushPendingSave(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.pendingSlot !== null) {
      void this._sendPending(true);
    }
  }

  private async _sendPending(keepalive = false): Promise<void> {
    if (this.pendingSlot === null) return;
    const slot = this.pendingSlot;
    const data = this.pendingData;
    this.pendingSlot = null;
    this.pendingData = null;
    try {
      await fetch(`${getApiBase()}/api/saves/${slot}`, {
        method: 'PUT',
        headers: this.authHeaders(),
        body: JSON.stringify(data),
        keepalive,
      });
    } catch { /* offline — ignore */ }
  }

  /** Fetch current resources from server. Returns null if not logged in or on error. */
  async fetchResources(): Promise<{ gold: number; loot: number } | null> {
    const auth = AuthService.getInstance();
    if (!auth.isLoggedIn()) return null;
    try {
      const res = await fetch(`${getApiBase()}/api/resources`, { headers: this.authHeaders() });
      if (!res.ok) return null;
      return await res.json() as { gold: number; loot: number };
    } catch { return null; }
  }

  /** Update specific resource fields on the server. Returns true on success. */
  async updateResources(patch: Partial<{ gold: number; loot: number }>): Promise<boolean> {
    const auth = AuthService.getInstance();
    if (!auth.isLoggedIn()) return false;
    try {
      const res = await fetch(`${getApiBase()}/api/resources`, {
        method: 'PATCH',
        headers: this.authHeaders(),
        body: JSON.stringify(patch),
      });
      return res.ok;
    } catch { return false; }
  }

}
